import type { ReactNode } from 'react';

import type { ScriptElement, TableContent } from '../../schema';

/**
 * A grid of plain-text cells (Word/Excel-style). Row/column counts are derived
 * from `content.cells`. Grid lines are drawn as a 1px gap over a border-colour
 * background with the cells painting on top, so borders never double up. The
 * cell text uses `style.fontSize` / `style.color` / `style.fontFamily`, defaulting
 * to the board font (Kalam) so cells match ordinary text elements everywhere.
 *
 * `renderCell` is the admin's editing seam: when supplied it renders an editable
 * node per cell instead of plain text, so the authoring canvas reuses this exact
 * layout rather than duplicating it. The player/teach call it without the hook.
 */
export function TableElement({
  element,
  fill,
  renderCell,
}: {
  element: ScriptElement;
  fill?: boolean;
  renderCell?: (row: number, col: number, text: string) => ReactNode;
}) {
  const content = element.content as TableContent | undefined;
  const cells = content?.cells ?? [];
  const rows = cells.length;
  const cols = rows > 0 ? Math.max(...cells.map((r) => r.length)) : 0;
  if (rows === 0 || cols === 0) return null;

  const border = content?.borderColor ?? '#cbd5e1';
  const size = (element.style?.fontSize as number | undefined) ?? 14;
  const color = (element.style?.color as string | undefined) ?? '#1f2937';
  const family = (element.style?.fontFamily as string | undefined) ?? 'Kalam, cursive';
  const header = content?.headerRow ?? false;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
        gap: 1,
        background: border,
        border: `1px solid ${border}`,
        borderRadius: 8,
        overflow: 'hidden',
        width: '100%',
        fontFamily: family,
        ...(fill ? { height: '100%' } : null),
      }}
    >
      {cells.map((row, r) =>
        Array.from({ length: cols }, (_, c) => {
          const text = row[c] ?? '';
          const isHeader = header && r === 0;
          return (
            <div
              key={`${r}-${c}`}
              style={{
                background: isHeader ? '#f1f5f9' : '#ffffff',
                padding: '6px 8px',
                fontSize: size,
                color,
                fontWeight: isHeader ? 600 : 400,
                lineHeight: 1.35,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'flex-start',
              }}
            >
              {renderCell ? renderCell(r, c, text) : text}
            </div>
          );
        }),
      )}
    </div>
  );
}
